import { AlertDialog, BlobCtaButton, GlassChip, Typography, useToast } from "@cupcodev/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { AvailabilityDay, CustomerMeeting, MeetingsOverview } from "../../shared/meetings/meetings";
import { EmptyState, NaveIcon, Notice, PageHeader, Panel, SectionSkeleton } from "../../ui/NavePage";
import { MonthCalendar, isoDate, type CalendarMonth } from "../../ui/MonthCalendar";
import { useI18n } from "../i18n/I18nProvider";
import { useScenario } from "../scenario/ScenarioProvider";
import { brazilianHolidays } from "../../shared/meetings/holidays";
import { AgendaAvailabilityEditor } from "./AgendaAvailabilityEditor";

const todayIn = (timeZone: string): string => new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const monthOf = (date: string): CalendarMonth => ({ year: Number(date.slice(0, 4)), month: Number(date.slice(5, 7)) - 1 });
const monthIndex = (value: CalendarMonth): number => value.year * 12 + value.month;
const slotEnd = (time: string, minutes: number): string => {
  const total = Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5)) + minutes;
  return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};
const lastDay = (value: CalendarMonth): number => new Date(Date.UTC(value.year, value.month + 1, 0)).getUTCDate();

export const AgendaPage = () => {
  const { runtime, session, activeOrganizationId } = useScenario();
  const { t, locale } = useI18n();
  const { toast } = useToast();
  const [overview, setOverview] = useState<MeetingsOverview>();
  const [loadFailed, setLoadFailed] = useState(false);
  const [cursor, setCursor] = useState<CalendarMonth>();
  const [days, setDays] = useState<readonly AvailabilityDay[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>();
  const [selectedTime, setSelectedTime] = useState<string>();
  const [topic, setTopic] = useState("");
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [cancelTarget, setCancelTarget] = useState<CustomerMeeting>();
  const [reload, setReload] = useState(0);
  const clientId = session?.activeClient?.id;
  const isAdmin = session?.platform?.isPlatformAdmin === true;

  useEffect(() => {
    let cancelled = false;
    setOverview(undefined); setLoadFailed(false);
    if (!clientId || !runtime.experience.getMeetings) { setLoadFailed(true); return; }
    void runtime.experience.getMeetings().then((result) => {
      if (cancelled) return;
      if (!result.ok) { setLoadFailed(true); return; }
      setOverview(result.value);
      setCursor((current) => current ?? monthOf(todayIn(result.value.timeZone)));
    }).catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, [runtime, clientId, activeOrganizationId.value, reload]);

  useEffect(() => {
    let cancelled = false;
    if (!cursor || !overview?.canBook || !runtime.experience.getAvailability) { setDays([]); return; }
    const from = isoDate(cursor.year, cursor.month, 1), to = isoDate(cursor.year, cursor.month, lastDay(cursor));
    void runtime.experience.getAvailability({ from, to }).then((result) => { if (!cancelled) setDays(result.ok ? result.value : []); }).catch(() => { if (!cancelled) setDays([]); });
    return () => { cancelled = true; };
  }, [runtime, cursor, overview?.canBook, reload]);

  const availableDates = useMemo(() => new Set(days.map((day) => day.date)), [days]);
  const holidays = useMemo(() => new Map(cursor ? brazilianHolidays(cursor.year).map((holiday) => [holiday.date, t(`agenda.holiday.${holiday.key}`)] as const) : []), [cursor?.year, t]);

  // Holidays are shown but never bookable; preselect the first bookable day so slots appear without an extra click.
  useEffect(() => {
    const bookable = days.filter((day) => !holidays.has(day.date));
    if (bookable.length === 0) return;
    setSelectedDate((current) => (current !== undefined && bookable.some((day) => day.date === current) ? current : bookable[0]?.date));
  }, [days, holidays]);
  const slots = days.find((day) => day.date === selectedDate)?.slots ?? [];
  const long = (date: string): string => new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" }).format(new Date(`${date}T00:00:00Z`));
  const when = (meeting: Pick<CustomerMeeting, "date" | "time">): string => `${long(meeting.date)} · ${meeting.time}`;

  const pickDate = useCallback((date: string) => { setSelectedDate(date); setSelectedTime(undefined); setError(undefined); setRequestId(crypto.randomUUID()); }, []);

  // Booking is confirmed in a dialog first (date, time and topic) so a stray click never books a meeting.
  const [reviewOpen, setReviewOpen] = useState(false);
  const book = async (): Promise<void> => {
    if (busy || !selectedDate || !selectedTime || !runtime.experience.bookMeeting) return;
    setBusy(true); setError(undefined);
    try {
      const result = await runtime.experience.bookMeeting({ date: selectedDate, time: selectedTime, requestId, ...(topic.trim() ? { topic: topic.trim() } : {}) });
      if (!result.ok) { setError(result.error.code); if (result.error.code === "CONFLICT") { setSelectedTime(undefined); setReload((v) => v + 1); } return; }
      toast({ variant: "success", title: t("agenda.booked", { when: when(result.value) }), description: t("agenda.booked.email") });
      setSelectedDate(undefined); setSelectedTime(undefined); setTopic(""); setRequestId(crypto.randomUUID()); setReload((v) => v + 1);
    } catch { setError("DEPENDENCY_UNAVAILABLE"); } finally { setBusy(false); }
  };

  const cancel = async (): Promise<void> => {
    const target = cancelTarget;
    if (!target || !runtime.experience.cancelMeeting) return;
    try {
      const result = await runtime.experience.cancelMeeting(target.meetingId);
      if (result.ok) toast({ variant: "success", title: t("agenda.cancelled") });
      else toast({ variant: "error", title: t("agenda.error.unavailable") });
    } catch { toast({ variant: "error", title: t("agenda.error.unavailable") }); }
    setCancelTarget(undefined); setReload((v) => v + 1);
  };

  const today = overview ? monthOf(todayIn(overview.timeZone)) : undefined;
  const limit = today && overview ? (() => { const date = new Date(Date.UTC(today.year, today.month, 1)); date.setUTCDate(date.getUTCDate() + overview.horizonDays); return monthOf(date.toISOString().slice(0, 10)); })() : undefined;
  const errorKey = error === "CONFLICT" ? "agenda.error.taken" : error === "VALIDATION_ERROR" ? "agenda.error.validation" : error === "FORBIDDEN" ? "agenda.error.forbidden" : "agenda.error.unavailable";

  return <div className="nave-page">
    <PageHeader icon="lucide:calendar-days" title={t("agenda.title")} description={t("agenda.subtitle")} />
    {!overview && !loadFailed ? <SectionSkeleton label={t("state.loading")} /> : loadFailed || !overview ? <EmptyState icon="lucide:calendar-x">{t("agenda.unavailable")}</EmptyState> : <>
      <Panel icon="lucide:calendar-clock" title={t("agenda.upcoming.title")}>
        {overview.meetings.length === 0 ? <p className="empty-state"><NaveIcon name="lucide:calendar-check" />{t("agenda.upcoming.empty")}</p> : <ul className="nave-agenda-list">
          {overview.meetings.map((meeting) => <li key={meeting.meetingId} className="nave-agenda-item">
            <div className="nave-agenda-when">
              <Typography as="strong" variant="title-sm">{long(meeting.date)}</Typography>
              <span className="nave-agenda-meta"><NaveIcon name="lucide:clock" />{meeting.time} · {t("agenda.duration", { minutes: meeting.durationMinutes })}</span>
              {meeting.topic ? <span className="nave-agenda-topic">{meeting.topic}</span> : null}
            </div>
            <GlassChip variant={meeting.status === "CONFIRMED" ? "success" : "purple"}>{t(`agenda.status.${meeting.status}`)}</GlassChip>
            <div className="nave-agenda-actions">
              {meeting.videoUrl ? <a className="nave-agenda-join" href={meeting.videoUrl} target="_blank" rel="noopener noreferrer"><NaveIcon name="lucide:video" />{t("agenda.join")}</a> : null}
              {overview.canBook ? <BlobCtaButton type="button" size="sm" variant="secondary" onClick={() => setCancelTarget(meeting)}>{t("agenda.cancel")}</BlobCtaButton> : null}
            </div>
          </li>)}
        </ul>}
      </Panel>
      <Panel icon="lucide:calendar-plus" title={t("agenda.book.title")} className="nave-agenda-panel">
        {!overview.canBook ? <Notice>{t("agenda.book.noPermission")}</Notice> : !cursor || !today || !limit ? null : <>
          <div className="nave-agenda-book">
            <div className="nave-agenda-box">
            <MonthCalendar cursor={cursor} availableDates={availableDates} selected={selectedDate} today={todayIn(overview.timeZone)} holidays={holidays} locale={locale}
              canGoBack={monthIndex(cursor) > monthIndex(today)} canGoForward={monthIndex(cursor) < monthIndex(limit)}
              previousLabel={t("calendar.previous")} nextLabel={t("calendar.next")} onSelect={pickDate}
              onMonthChange={(delta) => { setCursor({ year: cursor.year + Math.floor((cursor.month + delta) / 12), month: (cursor.month + delta + 12) % 12 }); setSelectedDate(undefined); setSelectedTime(undefined); }} />
            <ul className="nave-cal-legend" aria-label={t("agenda.legend.title")}>
              <li data-kind="available">{t("agenda.legend.available")}</li>
              <li data-kind="holiday">{t("agenda.legend.holiday")}</li>
              <li data-kind="selected">{t("agenda.legend.selected")}</li>
            </ul>
            </div>
            <div className="nave-agenda-box nave-agenda-slots">
              {days.length === 0 ? <Notice>{t("agenda.book.noSlots")}</Notice> : !selectedDate ? <div className="nave-agenda-placeholder"><NaveIcon name="lucide:mouse-pointer-click" /><p>{t("agenda.book.pickDay")}</p></div> : <>
                <Typography as="h3" variant="title-sm">{t("agenda.book.pickTime", { day: long(selectedDate) })}</Typography>
                <ul className="nave-agenda-times" aria-label={t("agenda.book.pickTime", { day: long(selectedDate) })}>
                  {slots.map((slot) => {
                    const chosen = selectedTime === slot.time;
                    return <li key={slot.time} className="nave-agenda-slot" data-selected={chosen ? "true" : "false"}>
                      <span className="nave-agenda-slot-time">
                        <strong>{slot.time} – {slotEnd(slot.time, slot.durationMinutes)}</strong>
                        <small>{t("agenda.duration", { minutes: slot.durationMinutes })}</small>
                      </span>
                      <BlobCtaButton type="button" size="sm" variant={chosen ? "primary" : "secondary"} aria-pressed={chosen}
                        onClick={() => { setSelectedTime(slot.time); setError(undefined); }}>{chosen ? t("agenda.book.chosen") : t("agenda.book.select")}</BlobCtaButton>
                    </li>;
                  })}
                </ul>
              </>}
              {selectedDate && selectedTime ? <div className="nave-agenda-confirm">
                <p className="nave-prose">{t("agenda.book.selected", { day: long(selectedDate), time: selectedTime, minutes: slots.find((slot) => slot.time === selectedTime)?.durationMinutes ?? 0 })}</p>
                <label className="nave-field"><span>{t("agenda.book.topic")}</span><textarea rows={2} maxLength={500} value={topic} disabled={busy} onChange={(event) => setTopic(event.target.value)} /></label>
                <div className="nave-form-actions"><BlobCtaButton type="button" size="sm" loading={busy} disabled={busy} onClick={() => setReviewOpen(true)}>{t("agenda.book.confirm")}</BlobCtaButton></div>
              </div> : null}
              {error ? <Notice tone="warning">{t(errorKey)}</Notice> : null}
            </div>
          </div>
        </>}
      </Panel>
    </>}
    {isAdmin ? <AgendaAvailabilityEditor /> : null}
    <AlertDialog open={reviewOpen && selectedDate !== undefined && selectedTime !== undefined} onOpenChange={(open) => { if (!open) setReviewOpen(false); }}
      title={t("agenda.book.review.title")} description={selectedDate && selectedTime ? t("agenda.book.selected", { day: long(selectedDate), time: selectedTime, minutes: slots.find((slot) => slot.time === selectedTime)?.durationMinutes ?? 0 }) + (topic.trim() ? " \u2014 " + topic.trim() : "") : ""}
      confirmLabel={t("agenda.book.confirm")} cancelLabel={t("agenda.book.review.back")} onConfirm={async () => { setReviewOpen(false); await book(); }} />
    <AlertDialog open={cancelTarget !== undefined} onOpenChange={(open) => { if (!open) setCancelTarget(undefined); }} variant="destructive"
      title={t("agenda.cancel.title")} description={cancelTarget ? t("agenda.cancel.description", { when: when(cancelTarget) }) : ""}
      confirmLabel={t("agenda.cancel.confirm")} cancelLabel={t("agenda.cancel.keep")} onConfirm={cancel} />
  </div>;
};
